/**
 * FileTransferService - Virtual Clipboard File Transfer
 *
 * Uses delayed rendering: sends file manifest, server sets virtual clipboard,
 * then file data is fetched on-demand when Windows requests it during paste.
 *
 * For multiple files/directories: compresses to zip, server extracts after transfer.
 *
 * Two DataChannels:
 * - file-ctrl: Control messages (JSON)
 * - file-data: Binary file data
 */

import JSZip from 'jszip';
import { eventBus } from './EventBus';

// Debug logging - set to false to disable detailed logs
const DEBUG_FILE_TRANSFER = false;
const log = DEBUG_FILE_TRANSFER ? console.log.bind(console) : () => {};
const logError = console.error.bind(console); // Errors always show

// Control message types
interface FileTransferControlMessage {
  type: 'file_transfer';
  action: 'manifest' | 'read' | 'ack' | 'error' | 'cancel' | 'complete' | 'paste_status' | 'upload_ack' | 'upload_error' | 'upload_done';
  transferId?: string;
  files?: Array<{
    id: number;
    name?: string;
    path: string;
    size: number;
    isDir: boolean;
  }>;
  // For zipped transfers
  isZipped?: boolean;
  originalFiles?: Array<{
    name: string;
    path: string;
    size: number;
    isDir: boolean;
  }>;
  fileIndex?: number;
  offset?: number;
  size?: number;
  totalSize?: number;
  status?: string; // For paste_status: 'completed' | 'cancelled' | 'error'
  error?: string;
  path?: string; // For upload_ack/upload_error
}

// Transfer status
export enum TransferStatus {
  PENDING = 'pending',
  TRANSFERRING = 'transferring',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

// Per-file info
export interface FileInfo {
  id: number;
  name: string;
  path: string;
  size: number;
  transferredBytes: number;
  status: 'pending' | 'transferring' | 'completed' | 'failed';
}

// Transfer info for UI
export interface TransferInfo {
  id: string;
  fileName: string;
  totalFiles: number;
  totalSize: number;
  transferredBytes: number;
  status: TransferStatus;
  startTime: number;
  files: FileInfo[];
}

class FileTransferService {
  private pc: RTCPeerConnection | null = null;
  private ctrlChannel: RTCDataChannel | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private isInitialized = false;

  // File storage for on-demand reading
  private files: Map<number, File> = new Map();
  private currentTransferId: string | null = null;
  private currentTransfer: TransferInfo | null = null;

  // Request queue to prevent concurrent handling
  private requestQueue: FileTransferControlMessage[] = [];
  private isProcessingRequest = false;

  // Channel creation lock to prevent race conditions
  private channelCreationPromise: Promise<boolean> | null = null;

  // Host channel listener registered flag
  private hostChannelListenerRegistered = false;

  // Upload ACK tracking for Pull mode
  private uploadAckCallbacks: Map<string, (offset: number) => void> = new Map();
  private uploadErrorCallbacks: Map<string, (error: string) => void> = new Map();

  /**
   * Initialize file transfer service
   */
  initialize(pc: RTCPeerConnection): void {
    // Always update PC reference (may be new connection after reconnect)
    if (this.pc !== pc) {
      // Close old channels if PC changed
      if (this.ctrlChannel) {
        this.ctrlChannel.close();
        this.ctrlChannel = null;
      }
      if (this.dataChannel) {
        this.dataChannel.close();
        this.dataChannel = null;
      }
      this.pc = pc;
      log('[FileTransfer] PC reference updated');
    }

    // Listen for host-created file channels (used on reconnection when browser can't create channels)
    if (!this.hostChannelListenerRegistered) {
      this.hostChannelListenerRegistered = true;
      eventBus.on('fileTransfer:hostChannel', (ev: { label: string; channel: RTCDataChannel }) => {
        this.acceptHostChannel(ev.label, ev.channel);
      });
    }

    this.isInitialized = true;
    log('[FileTransfer] Initialized, pc.connectionState:', pc.connectionState);
  }

  /**
   * Accept a host-created DataChannel (file-ctrl or file-data)
   * Used when reconnecting - the host creates channels and browser receives them via ondatachannel
   */
  private acceptHostChannel(label: string, channel: RTCDataChannel): void {
    if (label === 'file-ctrl') {
      if (this.ctrlChannel && this.ctrlChannel.readyState === 'open') {
        log('[FileTransfer] Already have open ctrl channel, ignoring host channel');
        return;
      }
      if (this.ctrlChannel) this.ctrlChannel.close();
      this.ctrlChannel = channel;
      log('[FileTransfer] Accepted host-created file-ctrl channel');
    } else if (label === 'file-data') {
      if (this.dataChannel && this.dataChannel.readyState === 'open') {
        log('[FileTransfer] Already have open data channel, ignoring host channel');
        return;
      }
      if (this.dataChannel) this.dataChannel.close();
      this.dataChannel = channel;
      log('[FileTransfer] Accepted host-created file-data channel');
    }
    this.setupChannelEvents();
  }

  /**
   * Create DataChannels for file transfer (on-demand)
   */
  private createDataChannels(): boolean {
    // If we already have open channels (e.g., host-created), just use them
    if (this.ctrlChannel?.readyState === 'open' && this.dataChannel?.readyState === 'open') {
      log('[FileTransfer] Channels already open, reusing');
      return true;
    }

    if (!this.pc) {
      logError('[FileTransfer] Cannot create DataChannels: no PC reference');
      return false;
    }

    // Check connection state - can only create DataChannel when connection is active
    const signalingState = this.pc.signalingState;
    const connectionState = this.pc.connectionState;

    if (signalingState === 'closed' ||
        connectionState === 'closed' ||
        connectionState === 'failed') {
      console.warn(`[FileTransfer] Cannot create DataChannels: signalingState=${signalingState}, connectionState=${connectionState}. Will use host-created channels.`);
      return false;
    }

    // Additional check - should be connected
    if (connectionState !== 'connected') {
      console.warn(`[FileTransfer] PC not fully connected yet: ${connectionState}, trying anyway...`);
    }

    // Close existing channels
    if (this.ctrlChannel) {
      this.ctrlChannel.close();
      this.ctrlChannel = null;
    }
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }

    try {
      // Control channel - reliable, ordered
      // priority: low - 控制消息优先级低于输入但高于数据
      this.ctrlChannel = this.pc.createDataChannel('file-ctrl', {
        ordered: true,
        priority: 'low',
      });

      // Data channel - reliable, ordered
      // priority: very-low - 批量数据传输使用最低优先级，避免阻塞其他通道
      this.dataChannel = this.pc.createDataChannel('file-data', {
        ordered: true,
        priority: 'very-low',
      });

      this.setupChannelEvents();
      return true;
    } catch (e) {
      logError('[FileTransfer] Failed to create DataChannels:', e);
      return false;
    }
  }

  /**
   * Setup DataChannel event handlers
   */
  private setupChannelEvents(): void {
    if (this.ctrlChannel) {
      this.ctrlChannel.onopen = () => {
        log('[FileTransfer] Control channel OPEN, id:', this.ctrlChannel?.id);
        eventBus.emit('fileTransfer:ready', true);
        // Notify clipboard download service when both channels are ready
        this.notifyClipboardDownloadChannels();
      };

      this.ctrlChannel.onclose = () => {
        log('[FileTransfer] Control channel CLOSED');
        eventBus.emit('fileTransfer:ready', false);
        // Mark current transfer as failed if in progress
        if (this.currentTransfer && this.currentTransfer.status === TransferStatus.TRANSFERRING) {
          this.handleError('Control channel closed unexpectedly');
        }
      };

      this.ctrlChannel.onerror = (e) => {
        logError('[FileTransfer] Control channel error:', e);
      };

      this.ctrlChannel.onmessage = (event) => {
        this.handleControlMessage(event.data);
      };

      // Monitor buffered amount
      this.ctrlChannel.bufferedAmountLowThreshold = 64 * 1024;
      this.ctrlChannel.onbufferedamountlow = () => {
        log('[FileTransfer] Control channel buffer low');
      };
    }

    if (this.dataChannel) {
      this.dataChannel.onopen = () => {
        log('[FileTransfer] Data channel OPEN, id:', this.dataChannel?.id);
        // Notify clipboard download service when both channels are ready
        this.notifyClipboardDownloadChannels();
      };

      this.dataChannel.onclose = () => {
        log('[FileTransfer] Data channel CLOSED');
        // Mark current transfer as failed if in progress
        if (this.currentTransfer && this.currentTransfer.status === TransferStatus.TRANSFERRING) {
          this.handleError('Data channel closed unexpectedly');
        }
      };

      this.dataChannel.onerror = (e) => {
        logError('[FileTransfer] Data channel error:', e);
      };

      // Monitor buffered amount for flow control
      this.dataChannel.bufferedAmountLowThreshold = 256 * 1024;
      this.dataChannel.onbufferedamountlow = () => {
        log('[FileTransfer] Data channel buffer low, can send more');
      };
    }
  }

  /**
   * Handle control messages from server
   */
  private handleControlMessage(data: string | ArrayBuffer): void {
    // Skip binary data - it should go to data channel
    if (data instanceof ArrayBuffer) {
      console.warn(`[FileTransfer] Received binary data on control channel (${data.byteLength} bytes), ignoring`);
      return;
    }

    // Skip empty strings
    if (!data || data.trim().length === 0) {
      return;
    }

    try {
      const msg = JSON.parse(data) as FileTransferControlMessage;

      if (msg.type !== 'file_transfer') {
        // Silently ignore non-file_transfer messages (e.g., clipboard_download)
        return;
      }

      log(`[FileTransfer] CTRL MESSAGE: action=${msg.action}, fileIndex=${msg.fileIndex}, offset=${msg.offset}, size=${msg.size}`);

      switch (msg.action) {
        case 'read':
          // Queue read requests to prevent concurrent handling
          this.queueReadRequest(msg);
          break;
        case 'ack':
          log('[FileTransfer] Server acknowledged manifest');
          if (this.currentTransfer) {
            this.currentTransfer.status = TransferStatus.TRANSFERRING;
            eventBus.emit('fileTransfer:update', { ...this.currentTransfer });
          }
          break;
        case 'upload_ack':
          // Handle ACK for binary upload (Pull mode)
          this.handleUploadAck(msg);
          break;
        case 'upload_error':
          // Handle error for binary upload
          this.handleUploadError(msg);
          break;
        case 'complete':
          this.handleComplete();
          break;
        case 'error':
          this.handleError(msg.error || 'Unknown error');
          break;
        case 'cancel':
          this.handleCancel();
          break;
        case 'paste_status':
          this.handlePasteStatus(msg);
          break;
      }
    } catch (e) {
      logError('[FileTransfer] Failed to parse control message:', e);
    }
  }

  /**
   * Queue a read request for sequential processing
   */
  private queueReadRequest(msg: FileTransferControlMessage): void {
    this.requestQueue.push(msg);
    this.processNextRequest();
  }

  /**
   * Process next request in queue
   */
  private async processNextRequest(): Promise<void> {
    if (this.isProcessingRequest || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingRequest = true;
    const msg = this.requestQueue.shift()!;

    try {
      await this.handleReadRequest(msg);
    } catch (e) {
      logError('[FileTransfer] Error processing request:', e);
    } finally {
      this.isProcessingRequest = false;
      // Process next request if any
      if (this.requestQueue.length > 0) {
        this.processNextRequest();
      }
    }
  }

  /**
   * Handle read request from server
   */
  // Max chunk size for WebRTC DataChannel (16KB is safe)
  private static readonly MAX_CHUNK_SIZE = 16 * 1024;

  private async handleReadRequest(msg: FileTransferControlMessage): Promise<void> {
    const fileIndex = msg.fileIndex ?? -1;
    const offset = msg.offset ?? 0;
    const size = msg.size ?? 0;

    const startTime = performance.now();
    log(`[FileTransfer] READ REQUEST: fileIndex=${fileIndex}, offset=${offset}, size=${size}, filesMap.size=${this.files.size}`);

    const file = this.files.get(fileIndex);
    if (!file) {
      console.error(`[FileTransfer] ERROR: File not found: index=${fileIndex}, available keys:`, Array.from(this.files.keys()));
      // Send empty response to unblock server
      this.dataChannel?.send(new ArrayBuffer(0));
      return;
    }

    log(`[FileTransfer] Found file: ${file.name}, size=${file.size}`);

    try {
      // Read the requested chunk
      log(`[FileTransfer] Reading file slice: offset=${offset}, end=${offset + size}`);
      const blob = file.slice(offset, offset + size);
      const buffer = await blob.arrayBuffer();
      const readTime = performance.now() - startTime;

      log(`[FileTransfer] Read ${buffer.byteLength} bytes in ${readTime.toFixed(1)}ms, sending in chunks...`);

      // Update status to transferring on first data request
      if (this.currentTransfer && this.currentTransfer.status === TransferStatus.PENDING) {
        this.currentTransfer.status = TransferStatus.TRANSFERRING;
      }

      // Update per-file status (create new object to trigger React update)
      if (this.currentTransfer) {
        const fileIdx = this.currentTransfer.files.findIndex(f => f.id === fileIndex);
        if (fileIdx >= 0 && this.currentTransfer.files[fileIdx].status === 'pending') {
          this.currentTransfer.files[fileIdx] = {
            ...this.currentTransfer.files[fileIdx],
            status: 'transferring'
          };
          // Emit immediately so UI can scroll to this file before it completes
          eventBus.emit('fileTransfer:update', { ...this.currentTransfer, files: [...this.currentTransfer.files] });
        }
      }

      // Send binary data in chunks (WebRTC has size limits)
      if (this.dataChannel?.readyState === 'open') {
        const data = new Uint8Array(buffer);
        let sent = 0;
        let chunkCount = 0;
        const MAX_BUFFER = 1024 * 1024; // 1MB max buffer

        // Send chunks with flow control
        while (sent < data.length) {
          // Check channel is still open
          if (this.dataChannel.readyState !== 'open') {
            console.error(`[FileTransfer] ERROR: Data channel closed during send, sent ${sent}/${data.length} bytes`);
            throw new Error('Data channel closed during transfer');
          }

          // Flow control - wait if buffer is too full
          while (this.dataChannel.bufferedAmount > MAX_BUFFER) {
            log(`[FileTransfer] Buffer full (${this.dataChannel.bufferedAmount}), waiting...`);
            await new Promise(resolve => setTimeout(resolve, 10));
            // Check channel still open
            if (this.dataChannel.readyState !== 'open') {
              throw new Error('Data channel closed while waiting for buffer');
            }
          }

          const chunkSize = Math.min(FileTransferService.MAX_CHUNK_SIZE, data.length - sent);
          const chunk = data.slice(sent, sent + chunkSize);
          this.dataChannel.send(chunk.buffer);
          sent += chunkSize;
          chunkCount++;
        }

        const totalTime = performance.now() - startTime;
        log(`[FileTransfer] SENT ${sent} bytes in ${chunkCount} chunks, total time: ${totalTime.toFixed(1)}ms, finalBuffer: ${this.dataChannel.bufferedAmount}`);

        // Update progress (create new file object to trigger React update)
        if (this.currentTransfer) {
          this.currentTransfer.transferredBytes += sent;
          // Update per-file progress
          const fileIdx = this.currentTransfer.files.findIndex(f => f.id === fileIndex);
          if (fileIdx >= 0) {
            const fileInfo = this.currentTransfer.files[fileIdx];
            const newTransferredBytes = fileInfo.transferredBytes + sent;
            const newStatus = newTransferredBytes >= fileInfo.size ? 'completed' : fileInfo.status;
            this.currentTransfer.files[fileIdx] = {
              ...fileInfo,
              transferredBytes: newTransferredBytes,
              status: newStatus
            };
          }

          // Check if all files are completed
          const allFilesCompleted = this.currentTransfer.files.every(f => f.status === 'completed');
          if (allFilesCompleted && this.currentTransfer.status !== TransferStatus.COMPLETED) {
            log('[FileTransfer] All files completed, marking transfer as complete');
            this.currentTransfer.status = TransferStatus.COMPLETED;
            eventBus.emit('ui:toast', { message: '文件传输完成', type: 'success' });
          }

          eventBus.emit('fileTransfer:update', { ...this.currentTransfer, files: [...this.currentTransfer.files] });
        }
      } else {
        console.error(`[FileTransfer] ERROR: Data channel not open, state=${this.dataChannel?.readyState}`);
      }
    } catch (e) {
      logError('[FileTransfer] ERROR: Failed to read file chunk:', e);
      // Send empty response to unblock server
      this.dataChannel?.send(new ArrayBuffer(0));
    }
  }

  /**
   * Handle transfer complete
   */
  private handleComplete(): void {
    log('[FileTransfer] Transfer complete');
    if (this.currentTransfer) {
      this.currentTransfer.status = TransferStatus.COMPLETED;
      eventBus.emit('fileTransfer:update', { ...this.currentTransfer });
      eventBus.emit('ui:toast', { message: 'File transfer completed', type: 'success' });
    }
    this.cleanup();
  }

  /**
   * Handle error
   */
  private handleError(error: string): void {
    logError('[FileTransfer] Error:', error);
    if (this.currentTransfer) {
      this.currentTransfer.status = TransferStatus.FAILED;
      eventBus.emit('fileTransfer:update', { ...this.currentTransfer });
      eventBus.emit('ui:toast', { message: `File transfer failed: ${error}`, type: 'error' });
    }
    this.cleanup();
  }

  /**
   * Handle cancel
   */
  private handleCancel(): void {
    log('[FileTransfer] Transfer cancelled');
    if (this.currentTransfer) {
      this.currentTransfer.status = TransferStatus.CANCELLED;
      eventBus.emit('fileTransfer:update', { ...this.currentTransfer });
    }
    this.cleanup();
  }

  /**
   * Handle upload ACK from server (Pull mode)
   */
  private handleUploadAck(msg: FileTransferControlMessage): void {
    const path = msg.path;
    const offset = msg.offset ?? 0;

    if (!path) return;

    const callback = this.uploadAckCallbacks.get(path);
    if (callback) {
      callback(offset);
    }
  }

  /**
   * Handle upload error from server
   */
  private handleUploadError(msg: FileTransferControlMessage): void {
    const path = msg.path;
    const error = msg.error || 'Unknown upload error';

    if (!path) return;

    // Notify error callback (sets uploadError flag)
    const errorCb = this.uploadErrorCallbacks.get(path);
    if (errorCb) {
      errorCb(error);
    }

    // Also reject the pending ACK wait so the upload fails immediately
    // instead of waiting 30s for ACK timeout
    const ackCb = this.uploadAckCallbacks.get(path);
    if (ackCb) {
      // Resolve with -1 to signal error; the upload loop will check uploadError flag
      this.uploadAckCallbacks.delete(path);
      ackCb(-1);
    }
  }

  /**
   * Handle paste status from Windows (when user pastes or cancels in Windows)
   */
  private handlePasteStatus(msg: FileTransferControlMessage): void {
    const status = msg.status;
    const fileIndex = msg.fileIndex ?? -1;
    const bytesTransferred = msg.size ?? 0;
    const totalBytes = msg.totalSize ?? 0;

    log(`[FileTransfer] Paste status: status=${status}, fileIndex=${fileIndex}, ${bytesTransferred}/${totalBytes} bytes`);

    if (!this.currentTransfer) {
      log('[FileTransfer] No current transfer for paste status');
      return;
    }

    switch (status) {
      case 'completed':
        // File paste completed successfully
        if (fileIndex >= 0 && fileIndex < this.currentTransfer.files.length) {
          this.currentTransfer.files[fileIndex] = {
            ...this.currentTransfer.files[fileIndex],
            status: 'completed',
            transferredBytes: bytesTransferred,
          };
        }

        // Check if all files completed
        const allCompleted = this.currentTransfer.files.every(f => f.status === 'completed');
        if (allCompleted) {
          this.currentTransfer.status = TransferStatus.COMPLETED;
          eventBus.emit('ui:toast', { message: '文件粘贴完成', type: 'success' });
          this.cleanup();
        }
        eventBus.emit('fileTransfer:update', { ...this.currentTransfer, files: [...this.currentTransfer.files] });
        break;

      case 'cancelled':
        // User cancelled paste in Windows
        log('[FileTransfer] Paste cancelled by user in Windows');
        this.currentTransfer.status = TransferStatus.CANCELLED;
        eventBus.emit('fileTransfer:update', { ...this.currentTransfer });
        eventBus.emit('ui:toast', { message: '粘贴已取消', type: 'warning' });
        this.cleanup();
        break;

      case 'error':
        // Error during paste
        log('[FileTransfer] Paste error');
        this.currentTransfer.status = TransferStatus.FAILED;
        eventBus.emit('fileTransfer:update', { ...this.currentTransfer });
        eventBus.emit('ui:toast', { message: '粘贴失败', type: 'error' });
        this.cleanup();
        break;
    }
  }

  /**
   * Send manifest (file list) to server
   */
  async sendManifest(fileList: FileList | File[]): Promise<string> {
    const files = Array.from(fileList);
    if (files.length === 0) {
      throw new Error('No files to transfer');
    }

    // Wait for channels to be ready (created by WebRTCService)
    if (!this.ctrlChannel || this.ctrlChannel.readyState !== 'open') {
      const ready = await this.ensureChannelsAsync();
      if (!ready) {
        throw new Error('File transfer channels not ready');
      }
    }

    const transferId = this.generateId();
    this.currentTransferId = transferId;

    // Store files for on-demand reading
    this.files.clear();
    const manifestFiles: FileTransferControlMessage['files'] = [];
    let totalSize = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Get relative path if available (for directory drops)
      const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;

      manifestFiles.push({
        id: i,
        name: relativePath,
        path: relativePath,
        size: file.size,
        isDir: false,
      });

      this.files.set(i, file);
      totalSize += file.size;
    }

    // Create file info array for UI
    const fileInfos: FileInfo[] = manifestFiles.map((mf, i) => ({
      id: mf.id,
      name: mf.name || files[i].name,
      path: mf.path,
      size: mf.size,
      transferredBytes: 0,
      status: 'pending' as const,
    }));

    // Create transfer info for UI
    this.currentTransfer = {
      id: transferId,
      fileName: files.length === 1 ? files[0].name : `${files.length} files`,
      totalFiles: files.length,
      totalSize,
      transferredBytes: 0,
      status: TransferStatus.PENDING,
      startTime: Date.now(),
      files: fileInfos,
    };

    eventBus.emit('fileTransfer:update', { ...this.currentTransfer });

    // Send manifest
    const msg: FileTransferControlMessage = {
      type: 'file_transfer',
      action: 'manifest',
      transferId,
      files: manifestFiles,
    };

    log('[FileTransfer] Sending manifest:', manifestFiles.length, 'files');

    if (!this.sendControl(msg)) {
      throw new Error('Failed to send manifest');
    }

    return transferId;
  }

  /**
   * Wait for channels to open (polling-based to avoid overwriting onopen)
   */
  private waitForChannels(): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const timeout = 5000;

      const checkReady = () => {
        // Check if both channels are open
        if (
          this.ctrlChannel?.readyState === 'open' &&
          this.dataChannel?.readyState === 'open'
        ) {
          log('[FileTransfer] Both channels are open');
          resolve();
          return;
        }

        // Check for timeout
        if (Date.now() - startTime > timeout) {
          const ctrlState = this.ctrlChannel?.readyState || 'null';
          const dataState = this.dataChannel?.readyState || 'null';
          reject(new Error(`DataChannel open timeout (ctrl=${ctrlState}, data=${dataState})`));
          return;
        }

        // Check again after 50ms
        setTimeout(checkReady, 50);
      };

      checkReady();
    });
  }

  // Max chunk size for control messages (10KB to be safe)
  private static readonly MAX_CTRL_CHUNK_SIZE = 10 * 1024;

  /**
   * Send control message (always chunked for simplicity)
   */
  private sendControl(msg: FileTransferControlMessage): boolean {
    if (this.ctrlChannel?.readyState !== 'open') {
      logError(`[FileTransfer] Cannot send control: channel state = ${this.ctrlChannel?.readyState || 'null'}`);
      return false;
    }

    const json = JSON.stringify(msg);
    const totalChunks = Math.ceil(json.length / FileTransferService.MAX_CTRL_CHUNK_SIZE);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * FileTransferService.MAX_CTRL_CHUNK_SIZE;
      const end = Math.min(start + FileTransferService.MAX_CTRL_CHUNK_SIZE, json.length);

      this.ctrlChannel.send(JSON.stringify({
        c: i,           // chunkIndex
        t: totalChunks, // total
        d: json.slice(start, end), // data
      }));
    }

    return true;
  }

  /**
   * Cancel current transfer
   */
  cancel(): void {
    if (this.currentTransferId) {
      this.sendControl({
        type: 'file_transfer',
        action: 'cancel',
        transferId: this.currentTransferId,
      });
    }
    this.handleCancel();
  }

  /** 中止二进制上传：通知 host 清空写队列并关闭所有上传写句柄
   *  （FileManager 上传无 manifest/currentTransferId，故不能用 cancel()） */
  cancelBinaryUpload(): void {
    this.sendControl({ type: 'file_transfer', action: 'cancel' });
  }

  /**
   * Cleanup after transfer
   */
  private cleanup(): void {
    this.files.clear();
    this.currentTransferId = null;
    // Clear request queue
    this.requestQueue = [];
    this.isProcessingRequest = false;
    // Keep channels open for next transfer
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `ft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * Get current transfer info
   */
  getCurrentTransfer(): TransferInfo | null {
    return this.currentTransfer;
  }

  /**
   * Check if ready for transfer
   */
  get isReady(): boolean {
    return (
      this.isInitialized &&
      this.pc !== null &&
      this.pc.connectionState === 'connected'
    );
  }

  /**
   * Reset service
   */
  reset(): void {
    this.cleanup();

    if (this.ctrlChannel) {
      this.ctrlChannel.close();
      this.ctrlChannel = null;
    }

    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }

    this.pc = null;
    this.isInitialized = false;
    this.currentTransfer = null;
  }

  /**
   * Notify clipboard download service when channels are ready
   */
  private notifyClipboardDownloadChannels(): void {
    if (this.ctrlChannel?.readyState === 'open' &&
        this.dataChannel?.readyState === 'open') {
      // Emit event for clipboard download service to set channels
      eventBus.emit('fileTransfer:channelsReady', {
        ctrl: this.ctrlChannel,
        data: this.dataChannel,
      });
    }
  }

  /**
   * Get the control channel (for clipboard download service)
   */
  getCtrlChannel(): RTCDataChannel | null {
    return this.ctrlChannel;
  }

  /**
   * Get the data channel (for clipboard download service)
   */
  getDataChannel(): RTCDataChannel | null {
    return this.dataChannel;
  }

  /**
   * Ensure channels are created (for clipboard download)
   */
  ensureChannels(): boolean {
    if (this.ctrlChannel?.readyState === 'open' && this.dataChannel?.readyState === 'open') {
      return true;
    }
    return this.createDataChannels();
  }

  /**
   * Ensure channels are created and open (async version with locking)
   * This prevents race conditions when multiple uploads start simultaneously
   */
  async ensureChannelsAsync(): Promise<boolean> {
    // Already open? Return immediately
    if (this.ctrlChannel?.readyState === 'open' && this.dataChannel?.readyState === 'open') {
      return true;
    }

    // If creation is in progress, wait for it
    if (this.channelCreationPromise) {
      log('[FileTransfer] Waiting for existing channel creation...');
      return this.channelCreationPromise;
    }

    // Start channel creation with lock
    this.channelCreationPromise = (async () => {
      try {
        // Double check after acquiring lock
        if (this.ctrlChannel?.readyState === 'open' && this.dataChannel?.readyState === 'open') {
          return true;
        }

        // Create channels
        if (!this.createDataChannels()) {
          return false;
        }

        // Wait for channels to open
        await this.waitForChannels();
        return true;
      } catch (e) {
        logError('[FileTransfer] ensureChannelsAsync failed:', e);
        return false;
      } finally {
        this.channelCreationPromise = null;
      }
    })();

    return this.channelCreationPromise;
  }

  // ============================================================
  // Binary Upload Protocol
  // ============================================================

  // Protocol constants
  private static readonly BINARY_MAGIC = 'FWRT';
  private static readonly BINARY_VERSION = 0x01;
  private static readonly FLAG_CREATE = 0x01;
  private static readonly FLAG_TRUNCATE = 0x02;
  private static readonly BINARY_HEADER_SIZE = 20;
  private static readonly BINARY_BUFFER_HIGH = 256 * 1024;   // 256KB - must wait when buffer exceeds this
  private static readonly BINARY_BUFFER_LOW = 64 * 1024;     // 64KB - resume when buffer drops below this
  private static readonly BINARY_CHUNK_SIZE = 64 * 1024;     // 64KB per chunk - safe for WebRTC

  /**
   * Check if binary upload is available
   */
  get isBinaryUploadReady(): boolean {
    return this.dataChannel?.readyState === 'open';
  }

  /**
   * Wait for data channel buffer to drain using polling
   * More reliable than bufferedamountlow event which may not fire in some cases
   */
  private waitForBinaryBuffer(): Promise<void> {
    const dc = this.dataChannel;
    if (!dc || dc.readyState !== 'open') {
      return Promise.reject(new Error('DataChannel not open'));
    }

    // If buffer is low enough, proceed immediately
    if (dc.bufferedAmount < FileTransferService.BINARY_BUFFER_HIGH) {
      return Promise.resolve();
    }

    // Poll until buffer drains
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const maxWait = 30000; // 30 second timeout

      const checkBuffer = () => {
        if (dc.readyState !== 'open') {
          reject(new Error('DataChannel closed while waiting for buffer'));
          return;
        }

        if (dc.bufferedAmount < FileTransferService.BINARY_BUFFER_LOW) {
          resolve();
          return;
        }

        if (Date.now() - startTime > maxWait) {
          reject(new Error('Buffer drain timeout'));
          return;
        }

        // Check again after a short delay
        setTimeout(checkBuffer, 10);
      };

      checkBuffer();
    });
  }

  /**
   * Build binary header for file write
   */
  private buildBinaryHeader(
    path: string,
    offset: number,
    dataLen: number,
    create: boolean,
    truncate: boolean
  ): ArrayBuffer {
    const pathBytes = new TextEncoder().encode(path);
    const totalLen = FileTransferService.BINARY_HEADER_SIZE + pathBytes.length;
    const buffer = new ArrayBuffer(totalLen);
    const view = new DataView(buffer);
    const uint8 = new Uint8Array(buffer);

    let pos = 0;

    // Magic (4 bytes)
    uint8.set(new TextEncoder().encode(FileTransferService.BINARY_MAGIC), pos);
    pos += 4;

    // Version (1 byte)
    view.setUint8(pos, FileTransferService.BINARY_VERSION);
    pos += 1;

    // Flags (1 byte)
    let flags = 0;
    if (create) flags |= FileTransferService.FLAG_CREATE;
    if (truncate) flags |= FileTransferService.FLAG_TRUNCATE;
    view.setUint8(pos, flags);
    pos += 1;

    // Path length (2 bytes, LE)
    view.setUint16(pos, pathBytes.length, true);
    pos += 2;

    // Offset (8 bytes, LE)
    view.setUint32(pos, offset >>> 0, true);
    view.setUint32(pos + 4, Math.floor(offset / 0x100000000), true);
    pos += 8;

    // Data length (4 bytes, LE)
    view.setUint32(pos, dataLen, true);
    pos += 4;

    // Path (variable)
    uint8.set(pathBytes, pos);

    return buffer;
  }

  // Sliding window size for ACK-based upload (number of in-flight chunks)
  private static readonly UPLOAD_WINDOW_SIZE = 4;
  // ACK timeout in milliseconds
  private static readonly ACK_TIMEOUT = 30000;

  /**
   * Upload a file using binary protocol with ACK-based flow control (Pull mode)
   * Uses sliding window to maintain throughput while ensuring reliable delivery
   * @param path - Destination path on server
   * @param file - File to upload
   * @param onProgress - Progress callback
   * @param shouldCancel - Cancellation checker
   */
  async uploadFileBinary(
    path: string,
    file: File,
    onProgress?: (bytesWritten: number, totalBytes: number) => void,
    shouldCancel?: () => boolean
  ): Promise<void> {
    if (!this.isBinaryUploadReady) {
      throw new Error('Binary upload not ready');
    }

    const dc = this.dataChannel!;
    const total = file.size;
    const chunkSize = FileTransferService.BINARY_CHUNK_SIZE;
    const windowSize = FileTransferService.UPLOAD_WINDOW_SIZE;

    // Handle empty files: send header with zero-length data to create empty file
    if (total === 0) {
      // 空文件零容忍治理：空文件只发 1 个 header 死等 1 个 ACK，无窗口无重传。
      // host 写线程偶发共享冲突会回 upload_error（经 handleUploadError 以 -1 提前 resolve），
      // 这里做有限快速重试；真正的 ACK 超时（host 长时间无响应）上抛，
      // 交由 store 的任务级自动重试（避免在此处串行死等多个 30s 超时）。
      const EMPTY_MAX_ATTEMPTS = 3;
      let emptyErr: string | null = null;
      this.uploadErrorCallbacks.set(path, (error) => { emptyErr = error; });
      try {
        for (let attempt = 0; attempt < EMPTY_MAX_ATTEMPTS; attempt++) {
          if (shouldCancel?.()) {
            throw new Error('UPLOAD_CANCELLED');
          }

          if (dc.readyState !== 'open') {
            throw new Error('DataChannel closed during upload');
          }

          emptyErr = null;
          // Build header with zero data length, create=true, truncate=true
          const header = this.buildBinaryHeader(path, 0, 0, true, true);
          dc.send(header);

          // host 报错时 handleUploadError 会以 -1 提前 resolve；不能误判成功
          const acked = await this.waitForUploadAck(path, 0);

          if (acked === -1 || emptyErr) {
            if (attempt < EMPTY_MAX_ATTEMPTS - 1) {
              await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
              continue;
            }
            throw new Error(`Server error: ${emptyErr || 'empty file upload failed'}`);
          }

          // 成功：通知 host 关闭该文件的持久写句柄
          this.sendControl({ type: 'file_transfer', action: 'upload_done', path });

          if (onProgress) {
            onProgress(0, 0);
          }
          return;
        }
      } finally {
        this.uploadAckCallbacks.delete(path);
        this.uploadErrorCallbacks.delete(path);
      }
      return;
    }

    // Sliding window state
    let sendOffset = 0;      // Next offset to send
    let ackedOffset = 0;     // Last ACKed offset
    let inFlight = 0;        // Number of chunks in flight
    let uploadError: string | null = null;

    // Register error callback
    this.uploadErrorCallbacks.set(path, (error) => {
      uploadError = error;
    });

    try {
      while (ackedOffset < total) {
        if (shouldCancel?.()) {
          throw new Error('UPLOAD_CANCELLED');
        }

        if (uploadError) {
          throw new Error(`Server error: ${uploadError}`);
        }

        if (dc.readyState !== 'open') {
          throw new Error('DataChannel closed during upload');
        }

        // Send chunks while window has space and there's data to send
        while (inFlight < windowSize && sendOffset < total) {
          if (shouldCancel?.()) {
            throw new Error('UPLOAD_CANCELLED');
          }

          const size = Math.min(chunkSize, total - sendOffset);
          const chunk = file.slice(sendOffset, sendOffset + size);
          const buffer = await chunk.arrayBuffer();

          // Build header + data
          const isFirst = sendOffset === 0;
          const header = this.buildBinaryHeader(path, sendOffset, buffer.byteLength, isFirst, isFirst);
          const combined = new Uint8Array(header.byteLength + buffer.byteLength);
          combined.set(new Uint8Array(header), 0);
          combined.set(new Uint8Array(buffer), header.byteLength);

          // Safety net: check bufferedAmount before sending
          if (dc.bufferedAmount > 1024 * 1024) { // 1MB safety limit
            await this.waitForBufferDrain(dc);
          }

          // Send binary
          dc.send(combined.buffer);

          sendOffset += size;
          inFlight++;
        }

        // Wait for at least one ACK to free up window space
        if (inFlight > 0) {
          const newAckedOffset = await this.waitForUploadAck(path, ackedOffset);

          if (uploadError) {
            throw new Error(`Server error: ${uploadError}`);
          }

          // Calculate how many chunks were ACKed
          const ackedBytes = newAckedOffset - ackedOffset;
          const ackedChunks = Math.ceil(ackedBytes / chunkSize);
          inFlight = Math.max(0, inFlight - ackedChunks);
          ackedOffset = newAckedOffset;

          if (onProgress) {
            onProgress(ackedOffset, total);
          }
        }
      }

      // 所有块已 ACK（host 已写完全部数据），通知 host 关闭该文件的持久写句柄
      this.sendControl({ type: 'file_transfer', action: 'upload_done', path });
    } finally {
      // Cleanup callbacks
      this.uploadAckCallbacks.delete(path);
      this.uploadErrorCallbacks.delete(path);
    }
  }

  /**
   * Wait for upload ACK from server
   * Returns the new ACKed offset
   */
  private waitForUploadAck(path: string, currentOffset: number): Promise<number> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.uploadAckCallbacks.delete(path);
        reject(new Error(`ACK timeout for ${path} at offset ${currentOffset}`));
      }, FileTransferService.ACK_TIMEOUT);

      this.uploadAckCallbacks.set(path, (offset: number) => {
        clearTimeout(timeout);
        // Keep callback registered for next ACK, just resolve this promise
        resolve(offset);
      });
    });
  }

  /**
   * Wait for DataChannel buffer to fully drain
   * This ensures all data is actually sent before we consider upload complete
   */
  private waitForBufferDrain(dc: RTCDataChannel): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const maxWait = 30000; // 30 seconds max

      const checkBuffer = () => {
        if (dc.readyState !== 'open') {
          reject(new Error('DataChannel closed while waiting for buffer drain'));
          return;
        }

        // Buffer is empty, all data sent
        if (dc.bufferedAmount === 0) {
          resolve();
          return;
        }

        if (Date.now() - startTime > maxWait) {
          reject(new Error('Buffer drain timeout - data may not have been sent'));
          return;
        }

        // Check again after a short delay
        setTimeout(checkBuffer, 10);
      };

      checkBuffer();
    });
  }
}

// Singleton
export const fileTransferService = new FileTransferService();
export default fileTransferService;
